import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { faPlus, faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import client from "../../client";
import routes from "../../routes";
import Table from "../components/Table";
import Pagination from "../components/Pagination";
import { NotFoundError } from "../../client/errors";
import PlaceHolder from "../components/PlaceHolder";

const Grais = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [graiList, setGraiList] = useState(undefined);
  const [sortBy, setSortBy] = useState(undefined);
  const [graiForDelete, setSerialForDelete] = useState(undefined);

  const params = Object.fromEntries(searchParams);
  const pageSize = Number(searchParams.get("pageSize") || "10");
  const page = Number(searchParams.get("page") || "1");
  const ordering = sortBy ? sortBy[0] : undefined;

  const handleGraiDelete = (grai) => {
    if (grai.seriesCount === 0) {
      setSerialForDelete(grai);
    } else {
      toast.error(
        "Before deletion, all related series must be deleted first."
      );
    }
  };

  const data =
    graiList?.results.map((result) => ({
      ...result,
      grai: <div className="font-monospace">{result.grai}</div>,
      action: (
        <div className="text-center">
          <Link to={routes.graiEdit(result.id)}>
            <FontAwesomeIcon className="icon--action me-2" icon={faPen} />
          </Link>
          <FontAwesomeIcon
            className="icon--action"
            icon={faTrash}
            onClick={() => handleGraiDelete(result)}
          />
        </div>
      ),
    })) ||
    Array(pageSize).fill({
      grai: <PlaceHolder />,
      name: <PlaceHolder />,
      totalSerials: <PlaceHolder />,
      action: <PlaceHolder />,
    });

  const columns = [
    {
      Header: "GRAI",
      accessor: "grai",
      disableSortBy: true,
    },
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Total Serials",
      accessor: "totalSerials",
    },
    {
      Header: "Action",
      accessor: "action",
      disableSortBy: true,
    },
  ];

  // State manipulators
  //

  const fetchGraiList = async () => {
    try {
      setGraiList(
        await client.grais.list({
          pageSize,
          page,
          ...(ordering && { ordering }),
        })
      );
    } catch (error) {
      if (error instanceof NotFoundError) {
        if (page !== 1) {
          setSearchParams({ ...params, page: 1 }, { replace: true });
          return;
        }
      }

      console.error();
      toast.error("Error");
    }
  };

  useEffect(() => {
    setGraiList(undefined);
    fetchGraiList();
  }, [searchParams, sortBy]);

  const deleteGrai = async () => {
    try {
      await client.grais.destroy(graiForDelete.id);

      setSerialForDelete(undefined);
      setSearchParams({ ...params, page: 1 }, { replace: true });
      setGraiList(undefined);
      fetchGraiList();
      return;
    } catch (error) {
      setSerialForDelete(undefined);
      console.error(error);
      throw error; // reject toast
    }
  };

  const handleRowClick = (row) => {
    if (graiList !== undefined) {
      navigate(routes.graiSeriesOverview(row.id));
    }
  };

  const handleTableChange = ({ sortBy }) => {
    setSortBy(sortBy);
  };

  // Rendering
  //

  return (
    <>
      <div className="grais">
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item active">
              <Link to={routes.graiOverview}>GRAIs</Link>
            </li>
          </ol>
        </nav>
        <h5 className="text-uppercase fw-bolder mt-4 mb-4">GRAI overview</h5>
        <Link className="btn btn-primary mb-4" to={routes.graiCreate}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Create new GRAI
        </Link>
        <Table
          columns={columns}
          data={data}
          onRowClick={handleRowClick}
          rowStyle={{ cursor: "pointer" }}
          onChange={handleTableChange}
          tableFoot={
            graiList !== undefined && (
              <Pagination
                count={graiList.count}
                page={page}
                pageSize={pageSize}
                pageSizeOptions={[
                  { value: 10, label: 10 },
                  { value: 20, label: 20 },
                  { value: 30, label: 30 },
                  ...([10, 20, 30].includes(pageSize)
                    ? []
                    : [{ value: pageSize, label: pageSize }]),
                ]}
                setPage={(page) =>
                  setSearchParams({ ...params, page }, { replace: true })
                }
                setPageAndSize={(newPage, newSize) =>
                  setSearchParams(
                    {
                      ...params,
                      page: newPage,
                      pageSize: newSize,
                    },
                    { replace: true }
                  )
                }
              />
            )
          }
        />
      </div>
      <Modal
        show={graiForDelete !== undefined}
        onHide={() => setSerialForDelete(undefined)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this entry?</Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setSerialForDelete(undefined)}
          >
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={() =>
              toast.promise(deleteGrai, {
                pending: "Deleting GRAI number",
                error: "Error while serial number",
                success: "GRAI was deleted",
              })
            }
          >
            Yes
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Grais;
